"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Hero from "@/components/Hero";
import FilterBar from "@/components/FilterBar";
import ProductCard from "@/components/ProductCard";
import { ShoppingBag, ArrowUp, Mail, Instagram, Twitter } from "lucide-react";

// 产品数据
const products = [
  {
    id: "1",
    name: "无线降噪耳机 Pro",
    description: "沉浸式音质体验，40小时续航，主动降噪技术",
    price: 1999,
    originalPrice: 2499,
    image: "https://images.unsplash.com/photo-1606741965509-717b9fdd6549?w=800",
    category: "音频",
    rating: 4.9,
    reviews: 328,
    isNew: true,
    aspectRatio: "tall" as const,
  },
  {
    id: "2",
    name: "智能手表 Ultra",
    description: "全天候健康监测，钛金属表壳，100米防水",
    price: 3299,
    image: "https://images.unsplash.com/photo-1758887952896-8491d393afe2?w=800",
    category: "穿戴",
    rating: 4.8,
    reviews: 256,
    isNew: true,
    aspectRatio: "square" as const,
  },
  {
    id: "3",
    name: "复古胶片相机",
    description: "经典机械设计，全画幅传感器，徕卡镜头",
    price: 12999,
    image: "https://images.unsplash.com/photo-1760604359549-8921b6139a1c?w=800",
    category: "摄影",
    rating: 4.9,
    reviews: 89,
    aspectRatio: "wide" as const,
  },
  {
    id: "4",
    name: "极简办公笔记本",
    description: "超薄机身设计，M3芯片，18小时续航",
    price: 8999,
    originalPrice: 9999,
    image: "https://images.unsplash.com/photo-1660218264783-0404dac6cd7e?w=800",
    category: "办公",
    rating: 4.7,
    reviews: 512,
    aspectRatio: "tall" as const,
  },
  {
    id: "5",
    name: "智能音箱 Mini",
    description: "360度环绕音效，智能语音助手，织物外观",
    price: 699,
    image: "https://images.unsplash.com/photo-1592515910799-d19d90bccfb8?w=800",
    category: "音频",
    rating: 4.6,
    reviews: 189,
    aspectRatio: "square" as const,
  },
  {
    id: "6",
    name: "平板电脑 Pro",
    description: "12.9英寸XDR显示屏，M2芯片，支持手写笔",
    price: 5999,
    image: "https://images.unsplash.com/photo-1625864667451-bd05ac7e633a?w=800",
    category: "办公",
    rating: 4.8,
    reviews: 423,
    isNew: true,
    aspectRatio: "tall" as const,
  },
  {
    id: "7",
    name: "机械键盘 Classic",
    description: "Cherry轴体，铝合金底座，RGB背光",
    price: 899,
    originalPrice: 1099,
    image: "https://images.unsplash.com/photo-1618384887929-16ec33fab9ef?w=800",
    category: "办公",
    rating: 4.7,
    reviews: 267,
    aspectRatio: "wide" as const,
  },
  {
    id: "8",
    name: "无线充电底座",
    description: "三合一设计，支持快充，极简美学",
    price: 399,
    image: "https://images.unsplash.com/photo-1586816879360-004f5b0c51e3?w=800",
    category: "配件",
    rating: 4.5,
    reviews: 145,
    aspectRatio: "square" as const,
  },
  {
    id: "9",
    name: "便携投影仪",
    description: "4K超清画质，自动对焦，内置音响",
    price: 3499,
    image: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=800",
    category: "家居",
    rating: 4.6,
    reviews: 98,
    aspectRatio: "wide" as const,
  },
  {
    id: "10",
    name: "咖啡机 Artisan",
    description: "意式浓缩萃取，奶泡系统，复古设计",
    price: 4599,
    image: "https://images.unsplash.com/photo-1517080319285-02181c8da27b?w=800",
    category: "家居",
    rating: 4.8,
    reviews: 234,
    aspectRatio: "tall" as const,
  },
  {
    id: "11",
    name: "真无线耳机",
    description: "空间音频，主动降噪，30小时续航",
    price: 1299,
    originalPrice: 1599,
    image: "https://images.unsplash.com/photo-1606741965326-cb990ae01bb2?w=800",
    category: "音频",
    rating: 4.7,
    reviews: 567,
    aspectRatio: "square" as const,
  },
  {
    id: "12",
    name: "智能台灯",
    description: "护眼光源，智能调光，无线充电底座",
    price: 599,
    image: "https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=800",
    category: "家居",
    rating: 4.5,
    reviews: 178,
    isNew: true,
    aspectRatio: "tall" as const,
  },
];

export default function Home() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [viewMode, setViewMode] = useState<"masonry" | "grid">("masonry");
  const [cartCount, setCartCount] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // 过滤产品
  const filteredProducts = products.filter((product) => {
    if (activeCategory === "all") return true;
    if (activeCategory === "new") return product.isNew;
    const categoryMap: { [key: string]: string } = {
      audio: "音频",
      wearable: "穿戴",
      home: "家居",
      camera: "摄影",
    };
    return product.category === categoryMap[activeCategory];
  });

  // 滚动监听
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="min-h-screen bg-[#F8FAFC]">
      {/* Hero区域 */}
      <Hero />

      {/* 筛选栏 */}
      <FilterBar
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* 产品瀑布流 */}
      <section className="mx-auto max-w-[1600px] px-4 py-12 sm:px-6 lg:px-8">
        <motion.div
          layout
          className={
            viewMode === "masonry"
              ? "columns-1 gap-6 space-y-6 sm:columns-2 lg:columns-3 xl:columns-4"
              : "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          }
        >
          <AnimatePresence mode="popLayout">
            {filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* 加载更多 */}
        <div className="mt-16 flex justify-center">
          <button className="group flex items-center gap-2 rounded-lg border border-[#1A1A1A] px-8 py-4 text-sm font-medium text-[#1A1A1A] transition-all duration-300 hover:bg-[#1A1A1A] hover:text-white">
            加载更多产品
          </button>
        </div>
      </section>

      {/* 订阅区域 */}
      <section className="bg-[#1A1A1A] py-20">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <h2
              className="text-3xl font-light text-white sm:text-4xl"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              订阅我们的精选资讯
            </h2>
            <p className="mx-auto max-w-lg text-[#94A3B8]">
              第一时间获取新品发布、独家优惠和生活方式灵感
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <input
                type="email"
                placeholder="输入您的邮箱"
                className="rounded-lg bg-white/10 px-6 py-4 text-white placeholder-[#94A3B8] outline-none transition-colors focus:bg-white/20 sm:w-80"
              />
              <button className="rounded-lg bg-[#3B82F6] px-8 py-4 text-sm font-medium text-white transition-colors hover:bg-[#1E40AF]">
                立即订阅
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 页脚 */}
      <footer className="border-t border-[#E2E8F0] bg-[#F8FAFC] py-16">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-8 lg:px-12">
          <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
            {/* 品牌 */}
            <div className="space-y-4">
              <div className="text-xl font-medium text-[#1A1A1A]">AURA</div>
              <p className="text-sm leading-relaxed text-[#64748B]">
                甄选全球优质好物，以极简设计理念，为您呈现品质与美学并存的精选产品。
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-[#94A3B8] transition-colors hover:text-[#3B82F6]">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="#" className="text-[#94A3B8] transition-colors hover:text-[#3B82F6]">
                  <Twitter className="h-5 w-5" />
                </a>
                <a href="#" className="text-[#94A3B8] transition-colors hover:text-[#3B82F6]">
                  <Mail className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* 产品分类 */}
            <div className="space-y-4">
              <div className="text-sm font-medium text-[#1A1A1A]">产品分类</div>
              <ul className="space-y-2 text-sm text-[#64748B]">
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">音频设备</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">智能穿戴</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">摄影器材</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">家居生活</a></li>
              </ul>
            </div>

            {/* 客户服务 */}
            <div className="space-y-4">
              <div className="text-sm font-medium text-[#1A1A1A]">客户服务</div>
              <ul className="space-y-2 text-sm text-[#64748B]">
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">配送说明</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">退换政策</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">常见问题</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">联系我们</a></li>
              </ul>
            </div>

            {/* 关于我们 */}
            <div className="space-y-4">
              <div className="text-sm font-medium text-[#1A1A1A]">关于我们</div>
              <ul className="space-y-2 text-sm text-[#64748B]">
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">品牌故事</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">加入我们</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">隐私政策</a></li>
                <li><a href="#" className="transition-colors hover:text-[#3B82F6]">使用条款</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-[#E2E8F0] pt-8 sm:flex-row">
            <div className="text-xs text-[#94A3B8]">
              © 2024 AURA. 保留所有权利。
            </div>
            <div className="flex gap-6 text-xs text-[#94A3B8]">
              <a href="#" className="transition-colors hover:text-[#1A1A1A]">隐私政策</a>
              <a href="#" className="transition-colors hover:text-[#1A1A1A]">使用条款</a>
              <a href="#" className="transition-colors hover:text-[#1A1A1A]">Cookie设置</a>
            </div>
          </div>
        </div>
      </footer>

      {/* 悬浮购物车 */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: showScrollTop ? 1 : 0, 
          scale: showScrollTop ? 1 : 0.8,
          y: showScrollTop ? 0 : 20
        }}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#3B82F6] text-white shadow-lg transition-colors hover:bg-[#1E40AF]"
        onClick={() => setCartCount(c => c + 1)}
      >
        <ShoppingBag className="h-5 w-5" />
        {cartCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-medium text-[#3B82F6]">
            {cartCount}
          </span>
        )}
      </motion.button>

      {/* 返回顶部 */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: showScrollTop ? 1 : 0, 
          scale: showScrollTop ? 1 : 0.8 
        }}
        onClick={scrollToTop}
        className="fixed bottom-24 right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#1A1A1A] shadow-lg transition-colors hover:bg-[#F1F5F9]"
      >
        <ArrowUp className="h-4 w-4" />
      </motion.button>
    </main>
  );
}
